import type React from 'react';
import { IntegrationConnectionStatus } from 'src/api/generated';
import type {
  TCPropertyImportPropertyScope,
  TCPropertyImportSyncMissingPolicy,
} from 'src/api/generated';
import { buildPropertyImportOptions } from 'src/modules/settings/utils/integration-import-options.util';
import {
  getMeliIntegrationConfigRecord,
  areMeliSellerContactWhatsAppSelectionsEqual,
  type FrontendMeliSellerContactWhatsAppSelection,
  type FrontendMeliIntegrationMode,
} from 'src/modules/shared/utils/integrations/meli-integration.util';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import type { IntegrationProviderId } from 'src/api/generated';
import type { useAlert } from 'src/contexts/alert-context';
import type { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type {
  useConnectIntegration,
  useEnqueueIntegrationSync,
  useIntegrations,
} from 'src/modules/shared/hooks/api/integrations/integrations';
import { extractApiErrorMessage } from 'src/modules/shared/utils/api/error-handler.util';

const PROVIDER_ID = 'meli' as IntegrationProviderId;

/**
 * Returns whether the local MercadoLibre integration form differs from the
 * currently persisted configuration.
 */
export const hasUnsavedChanges = (params: {
  isConnected: boolean;
  mode: FrontendMeliIntegrationMode;
  currentMode: FrontendMeliIntegrationMode;
  autoAssignEnabled: boolean;
  currentAutoAssignEnabled: boolean;
  syncMissingPolicy: TCPropertyImportSyncMissingPolicy;
  currentSyncMissingPolicy: TCPropertyImportSyncMissingPolicy;
  propertyScope: TCPropertyImportPropertyScope;
  currentPropertyScope: TCPropertyImportPropertyScope;
  sellerContactWhatsApp: FrontendMeliSellerContactWhatsAppSelection | null;
  currentSellerContactWhatsApp: FrontendMeliSellerContactWhatsAppSelection | null;
}): boolean => {
  if (!params.isConnected) {
    return false;
  }

  return (
    params.mode !== params.currentMode ||
    params.autoAssignEnabled !== params.currentAutoAssignEnabled ||
    params.syncMissingPolicy !== params.currentSyncMissingPolicy ||
    params.propertyScope !== params.currentPropertyScope ||
    !areMeliSellerContactWhatsAppSelectionsEqual(
      params.sellerContactWhatsApp,
      params.currentSellerContactWhatsApp
    )
  );
};

/**
 * Resolves the confirmation description key for a MercadoLibre mode change.
 */
export const getModeChangeDescriptionKey = (
  mode: FrontendMeliIntegrationMode
): TranslationKey =>
  mode === 'SYNC'
    ? 'settings.integrations.meli.mode.confirm.toSyncDescription'
    : 'settings.integrations.meli.mode.confirm.toPublishDescription';

interface RunMeliSyncParams {
  enqueueIntegrationSync: ReturnType<
    typeof useEnqueueIntegrationSync
  >['enqueueIntegrationSync'];
  refetch: ReturnType<typeof useIntegrations>['refetch'];
  showAlert: ReturnType<typeof useAlert>['showAlert'];
  t: ReturnType<typeof useI18n>['t'];
}

/**
 * Enqueues a MercadoLibre manual sync and surfaces the outcome to the user.
 */
export const runMeliSync = async ({
  enqueueIntegrationSync,
  refetch,
  showAlert,
  t,
}: RunMeliSyncParams): Promise<void> => {
  try {
    await enqueueIntegrationSync({
      providerId: PROVIDER_ID,
      body: {},
    });
    showAlert(
      t('settings.integrations.meli.actions.syncNow.success'),
      'success'
    );
  } catch (error) {
    const message = extractApiErrorMessage(error);
    const isSyncAlreadyInProgress = message
      .toLowerCase()
      .includes('sync already in progress');

    showAlert(message, isSyncAlreadyInProgress ? 'warning' : 'error');

    if (isSyncAlreadyInProgress) {
      await refetch();
    }
  }
};

interface SaveMeliConfigParams {
  meliConnection:
    | NonNullable<
        ReturnType<typeof useIntegrations>['integrations']
      >['items'][number]['connection']
    | null;
  autoAssignEnabled: boolean;
  syncMissingPolicy: TCPropertyImportSyncMissingPolicy;
  propertyScope: TCPropertyImportPropertyScope;
  mode: FrontendMeliIntegrationMode;
  sellerContactWhatsApp: FrontendMeliSellerContactWhatsAppSelection | null;
  connectIntegration: ReturnType<
    typeof useConnectIntegration
  >['connectIntegration'];
  refetch: ReturnType<typeof useIntegrations>['refetch'];
  showAlert: ReturnType<typeof useAlert>['showAlert'];
  t: ReturnType<typeof useI18n>['t'];
  setConfirmModeChangeOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Persists the MercadoLibre integration configuration currently edited in the
 * settings form.
 */
export const saveMeliConfig = async ({
  meliConnection,
  autoAssignEnabled,
  syncMissingPolicy,
  propertyScope,
  mode,
  sellerContactWhatsApp,
  connectIntegration,
  refetch,
  showAlert,
  t,
  setConfirmModeChangeOpen,
}: SaveMeliConfigParams): Promise<void> => {
  if (!meliConnection?.credentialId) {
    showAlert(t('settings.integrations.meli.mode.missingCredential'), 'error');
    return;
  }

  try {
    const currentConfig = getMeliIntegrationConfigRecord(meliConnection.config);
    const importOptions = buildPropertyImportOptions({
      autoAssignmentEnabled: autoAssignEnabled,
      syncMissingPolicy,
      propertyScope,
    });

    await connectIntegration({
      providerId: PROVIDER_ID,
      body: {
        credentialId: meliConnection.credentialId,
        config: {
          ...currentConfig,
          importOptions,
          mode,
          sellerContactWhatsApp,
        },
        syncFrequencyMinutes: meliConnection.syncFrequencyMinutes,
        status: IntegrationConnectionStatus.ACTIVE,
      },
    });

    showAlert(t('settings.integrations.meli.mode.saved'), 'success');
    setConfirmModeChangeOpen(false);
    await refetch();
  } catch (error) {
    const message = extractApiErrorMessage(error);
    showAlert(t('settings.integrations.meli.mode.error', { message }), 'error');
  }
};
